/**
 * La línea de tiempo HONESTA.  (US2, FR-015a, FR-015b)
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  Una línea agujereada y una completa TIENEN que distinguirse a simple      ║
 * ║  vista. Ese es el trabajo de este componente.                             ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 *   · observación  → un nodo con su estado y su hora.
 *   · "faltan pasos" → antes del nodo, un tramo PUNTEADO con el aviso: entre esas
 *     dos observaciones el bot recorrió estados que no pudimos ver.
 *   · hueco        → una banda aparte: el panel NO estuvo mirando ese rato.
 *
 * (shadcn/ui no trae una línea de tiempo: se escribe a mano — Principio IV lo
 * permite, "no reinventar primitivos" no es "no escribir nada".)
 */
import { Badge } from './ui/badge'
import { CountersCell } from './CountersCell'
import { hora } from '../lib/tiempo'
import type { EntradaTimeline } from '../lib/tipos'

export function Timeline({ entradas }: { entradas: EntradaTimeline[] }) {
    if (entradas.length === 0) {
        return <p className="text-sm text-neutral-600">Sin observaciones registradas.</p>
    }
    return (
        <ol className="space-y-0">
            {entradas.map((e, i) => (e.tipo === 'hueco' ? <Hueco key={i} e={e} /> : <Observacion key={i} e={e} />))}
        </ol>
    )
}

function Observacion({ e }: { e: Extract<EntradaTimeline, { tipo: 'observacion' }> }) {
    return (
        <li>
            {/* Aviso de "faltan pasos": el tramo hasta este nodo es punteado. */}
            {e.faltanPasos && (
                <div className="ml-[5px] flex items-center gap-2 border-l-2 border-dashed border-amber-600/70 py-1 pl-4 text-xs text-amber-500">
                    faltan pasos aquí{e.pasosSaltados > 0 ? ` (≥${e.pasosSaltados})` : ''} — el bot cambió de estado sin que lo viéramos
                </div>
            )}
            <div className="flex items-start gap-3 border-l-2 border-neutral-800 py-1.5 pl-4">
                <span className="-ml-[21px] mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-neutral-500 ring-4 ring-neutral-950" />
                <div className="flex flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium text-neutral-200">{e.state}</span>
                    <Badge tone="neutral">{e.flow}</Badge>
                    {e.counters && <CountersCell counters={e.counters} />}
                    <span className="ml-auto font-mono text-xs text-neutral-600">{hora(e.at)}</span>
                </div>
            </div>
        </li>
    )
}

function Hueco({ e }: { e: Extract<EntradaTimeline, { tipo: 'hueco' }> }) {
    return (
        <li>
            <div className="my-1 ml-[5px] flex items-center gap-2 rounded border border-dashed border-neutral-700 bg-neutral-900/40 py-2 pl-4 pr-3 text-xs text-neutral-500">
                <span className="text-neutral-600">⋯</span>
                hueco de observación
                <span className="font-mono text-neutral-500">
                    {hora(e.at)}–{hora(e.hasta)}
                </span>
                <span className="ml-auto text-neutral-700">el panel no estaba mirando</span>
            </div>
        </li>
    )
}
